import { useState } from "react";

import { useExamContext } from "@features/exam";
import { useAppStore } from "@hooks/useAppStore";
import {
	Box,
	Button,
	FEEDBACK_TOAST_DURATION_MS,
	MarkdownRenderer,
	useToast,
} from "@memorang/ui";
import { trackCustomEvent } from "analytics";
import { ScrollView } from "react-native";
/* -----------------UI--------------- */
import { Caption, TextInput, useTheme } from "react-native-paper";
import { dialogTexts } from "../constants";
import {
	type FeedbackType,
	buildOptionalFeedbackParams,
	sendFeedback,
} from "../helpers";

import { events } from "@constants/tracking";
/* -----------------Helpers & Hooks--------------- */
import i18n from "@helpers/lib/i18n";
import { isAndroid } from "@helpers/platform";
import useAppConfigIds from "@hooks/useAppConfigIds";
import useClerkUser from "@hooks/useClerkUser";
import { useTranslation } from "react-i18next";

const MAX_FEEDBACK_LENGTH = 1000;
const MAX_FEEDBACK_LENGTH_WARNING = 900;
const CHARACTER_LIMIT_THRESHOLD = 900;

type Props = {
	handleDismiss: () => void;
	view: string;
	feedbackSubtitle?: string;
	feedbackCta?: string;
	itemId?: string;
	itemFeedbackRating?: "positive" | "negative";
	sessionId?: number;
	threadId?: string;
	runId?: string;
	type?: FeedbackType;
	featureName?: string;
	responseId?: string;
	/** Free-form context about what the feedback targets; lands in the backend's appContext. */
	context?: string;
	/** SUMMATIVE or FORMATIVE; defaults to FORMATIVE when itemId is set. */
	sessionType?: string;
};

const FeedbackForm = (props: Props) => {
	const theme = useTheme();
	const [loading, setLoading] = useState(false);
	const [feedback, setFeedback] = useState<string>("");
	const { projectId, organizationId, productId } = useAppConfigIds();

	const { t } = useTranslation();

	const {
		currentExamContext: { examName },
	} = useExamContext();

	const { username, email } = useClerkUser();

	const companyName = useAppStore((store) => store.companyName);
	const appName = useAppStore((store) => store.app.name);

	const {
		handleDismiss,
		view,
		feedbackSubtitle,
		itemId,
		itemFeedbackRating,
		sessionId,
		threadId,
		runId,
		type = "Review" as const,
		featureName,
		feedbackCta,
		responseId,
		context,
		sessionType,
	} = props;
	const { showToast } = useToast();

	const isPremiumUser = false;
	const trimmedFeedback = feedback.trim();

	const {
		feedback: { body },
	} = dialogTexts;
	const handleSendFeedback = () => {
		setLoading(true);
		trackCustomEvent({
			eventName: events.feedbackSubmitted,
			source: "review_dialog",
		});

		const params = {
			userName: username ?? "",
			email: email!,
			appName,
			appLanguage: i18n.language,
			feedback: trimmedFeedback,
			view,
			isPremiumUser,
			examName,
			companyName,
			type,
			projectId,
			organizationId,
			productId,
			...buildOptionalFeedbackParams({
				sessionId,
				itemId,
				threadId,
				runId,
				rating: itemFeedbackRating,
				featureName,
				responseId,
				context,
				sessionType,
			}),
		};
		try {
			sendFeedback(params);
			showToast?.({
				message: t("feedback.successMessage"),
				duration: FEEDBACK_TOAST_DURATION_MS,
			});
		} finally {
			handleDismiss();
			setLoading(false);
		}
	};
	const SendFeedbackFAB = () => (
		<Button
			icon="send"
			uppercase={false}
			mode="contained"
			onPress={handleSendFeedback}
			loading={loading}
			disabled={loading || !trimmedFeedback}
		>
			{feedbackCta ?? t("dialog.feedback.cta")}
		</Button>
	);

	const handleTextChange = (text: string) => {
		if (text.length <= MAX_FEEDBACK_LENGTH) {
			setFeedback(text);
		}
	};

	const finalFeedbackSubtitle = feedbackSubtitle ?? t(body);

	const hasError = feedback.length > MAX_FEEDBACK_LENGTH_WARNING;
	return (
		<ScrollView
			contentContainerStyle={{
				gap: 16,
				// flexGrow: 1,
			}}
		>
			<MarkdownRenderer textAlign="center" text={finalFeedbackSubtitle} />
			<Box>
				<TextInput
					placeholder={t("dialog.feedback.placeholder")}
					mode="outlined"
					maxLength={MAX_FEEDBACK_LENGTH}
					value={feedback}
					multiline
					autoFocus
					onChangeText={handleTextChange}
					style={{
						backgroundColor: theme.colors.surface,
						height: 176,
						paddingTop: isAndroid ? 16 : 2,
					}}
					disabled={loading}
					error={hasError}
				/>
				{feedback.length >= CHARACTER_LIMIT_THRESHOLD && (
					<Caption
						style={{
							textAlign: "right",
						}}
					>
						{feedback.length}/{MAX_FEEDBACK_LENGTH}
					</Caption>
				)}
			</Box>
			<SendFeedbackFAB />
		</ScrollView>
	);
};

export default FeedbackForm;
